/* eslint-disable max-len */
/* eslint-disable import/no-unresolved */
import { isNull } from 'lodash';
import { IncomeStatementAllow } from 'api';
import {
  FIRST_ROW_TITLE,
  REVENUE_GROWTH_RATE_ALIAS,
  SECOND_ROW_ALIAS,
  THIRD_ROW_ALIAS,
} from 'common/constants/financials';
import { FinancialsPerformanceMetricsColumn, FinancialStatementPeriod } from 'common/types/financials/financials';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { getRowSpan } from 'pages/Financials/utilities';
import { getValueByPropOrFallback, gridShortDate, parseValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import getLTMRevenueGrowthRate, { NA_VALUE } from './getLTMRevenueGrowthRate';
import { FiscalYearData } from './types';
import { KpiConfigurationRow } from '../../kpi/config/types';

const getExpr = (expr: string, columnLegend: string) => (expr ? expr.replace(/@/g, `${columnLegend}`) : '');

const getRelativeExpr = (expr: string, columnLegend: string, columnIndex: number, alphabet: string[]) => {
  const FIRST_YEAR_COLUMN_LEGEND = 'A';
  if (FIRST_YEAR_COLUMN_LEGEND === columnLegend) {
    return '';
  }

  const previousColumnLegend = alphabet[columnIndex - 1];
  if (isNull(previousColumnLegend)) {
    return '';
  }

  return expr.replace(/#/g, `${columnLegend}`).replace(/@/g, `${previousColumnLegend}`);
};

// LTM revenue growth rate relative expression is handled in another place
const getExpressionToUse = ({
  rowAlias,
  rowExpr,
  columnLegend,
  columnIndex,
  alphabet,
}: {
  rowAlias: string;
  rowExpr: string;
  columnLegend: string;
  columnIndex: number;
  alphabet: string[];
}) =>
  rowAlias === REVENUE_GROWTH_RATE_ALIAS
    ? getRelativeExpr(rowExpr, columnLegend, columnIndex, alphabet)
    : getExpr(rowExpr, columnLegend);

const generateSecondRowConditionalProps = (params: {
  alias: string;
  alphabet: string[];
  columnIndex: number;
  columns: FinancialStatementPeriod[];
  dateInLTMOrNTM: boolean;
  expr: string;
  fiscalYearData: FinancialStatementPeriod[];
  format: RowFormat;
  ignoreExpr: boolean;
  isLTM: boolean;
  isLTMYear: boolean;
  mirrorExpression: string;
  value: string;
  incomeStatementColumns: FinancialStatementPeriod[];
}) => {
  const {
    alias,
    alphabet,
    columnIndex,
    columns,
    dateInLTMOrNTM,
    expr,
    fiscalYearData,
    format,
    ignoreExpr,
    isLTM,
    isLTMYear,
    mirrorExpression,
    value,
    incomeStatementColumns,
  } = params;

  let cellExpression = expr;
  let cellFormat: RowFormat | undefined = format;
  let cellIgnoreExpr = ignoreExpr;
  let cellTooltipMessages: string[] = [];
  let cellValue = value;

  if (alias === REVENUE_GROWTH_RATE_ALIAS && isLTM && isLTMYear && incomeStatementColumns?.length) {
    const LTMRevenueGrowthRate = getLTMRevenueGrowthRate({
      alphabet,
      columns: columns as FinancialsPerformanceMetricsColumn[],
      fiscalYearData: fiscalYearData as FiscalYearData,
      format: cellFormat,
      ignoreExpr: cellIgnoreExpr,
      value: cellValue,
      incomeStatementColumns: incomeStatementColumns as IncomeStatementAllow[],
    });

    cellExpression = LTMRevenueGrowthRate.expr as string;
    cellFormat = LTMRevenueGrowthRate.format as RowFormat;
    cellIgnoreExpr = LTMRevenueGrowthRate.ignoreExpr as boolean;
    cellTooltipMessages = LTMRevenueGrowthRate.tooltipMessages as string[];
    cellValue = LTMRevenueGrowthRate.value as string;
  }

  const secondRowConditionalProps = dateInLTMOrNTM
    ? {
      expr: mirrorExpression,
      ignoreExpr: false,
    }
    : {
      expr: cellExpression,
      ignoreExpr: cellIgnoreExpr,
    };

  let result = {
    ...secondRowConditionalProps,
    format: cellFormat,
    tooltipMessages: cellTooltipMessages,
    value: cellValue,
  };

  // If is the first FY column, then we need to set the value to N/A
  if (alias === REVENUE_GROWTH_RATE_ALIAS && columnIndex === 0) {
    result = {
      ...secondRowConditionalProps,
      ...(NA_VALUE(cellTooltipMessages) as any),
    };
  }

  return result;
};

const parser = async ({
  columns = [],
  rowConfig,
  fiscalYearData,
  tableData,
}: {
  columns: FinancialStatementPeriod[];
  rowConfig: KpiConfigurationRow[];
  fiscalYearData?: FiscalYearData;
  tableData: {
    incomeStatementSheet: SpreadsheetConfig | null;
  };
}) => {
  let cells = {};
  const incomeStatementColumns = tableData.incomeStatementSheet?.columns || [];
  const alphabet = alphabetGenerator([], columns.length);

  const getParsedValue = ({
    row,
    value,
    type,
    extraConditions,
  }: {
    row: KpiConfigurationRow;
    value: string;
    type: string;
    extraConditions: {
      isReportedQuarter: boolean;
      isLTMOrNTM: boolean;
      quarter: number;
    };
  }) => {
    const { isReportedQuarter, isLTMOrNTM, quarter } = extraConditions;
    // resolve particular cases
    if (row.alias === THIRD_ROW_ALIAS && isReportedQuarter) {
      return '';
    }
    if (row.alias === THIRD_ROW_ALIAS && isLTMOrNTM && !quarter) {
      return '';
    }
    if (row.alias === SECOND_ROW_ALIAS && isLTMOrNTM && !quarter) {
      return gridShortDate(value);
    }
    if (row.alias === THIRD_ROW_ALIAS && isLTMOrNTM && quarter) {
      return gridShortDate(value);
    }
    // generic resolve
    return parseValue(value, type, null, null, row.dbType);
  };

  // Parse body cells
  columns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];
    const { quarter, isNTM, isLTM, title, isLTMYear, isNTMYear } = column;

    const isLTMOrNTM = isNTM || isLTM;
    const isReportedQuarter = quarter && !isLTMOrNTM && title !== FIRST_ROW_TITLE;

    rowConfig.forEach((row, index) => {
      const rowNumber = index + 1;
      const key = columnLegend + rowNumber;
      const type = getValueByPropOrFallback(row, 'gridType', null);
      const expr = getExpressionToUse({
        rowAlias: row.alias as string,
        rowExpr: row.expr as string,
        columnLegend,
        columnIndex,
        alphabet,
      });

      const mirrorExpression = `=incomeStatement.${key}`;
      const dateInLTMOrNTM = row.alias === SECOND_ROW_ALIAS && (isLTMYear || isNTMYear);
      const columnValue =
        dateInLTMOrNTM && column.income_statement
          ? column.income_statement.reported_statement_date
          : column[row.alias as keyof FinancialStatementPeriod];

      const parsedValue = getParsedValue({
        row,
        value: columnValue as string,
        type,
        extraConditions: {
          isReportedQuarter: Boolean(isReportedQuarter),
          isLTMOrNTM: Boolean(isLTMOrNTM),
          quarter: Number(quarter),
        },
      });

      // Calculate LTM Revenue Growth Rate / Create second Row Conditional props
      const secondRowConditionalProps = generateSecondRowConditionalProps({
        alias: row.alias,
        alphabet,
        columnIndex,
        columns,
        dateInLTMOrNTM: Boolean(dateInLTMOrNTM),
        expr,
        fiscalYearData,
        format: row.format as RowFormat,
        ignoreExpr: row.ignoreExpr as boolean,
        isLTM: Boolean(isLTM),
        isLTMYear: Boolean(isLTMYear),
        mirrorExpression,
        value: parsedValue,
        incomeStatementColumns,
      });

      const isAdditionalHistoricalYear = getValueByPropOrFallback(column, 'isAdditionalHistoricalYear', false);
      const showAdditionalHistoricalYear = getValueByPropOrFallback(column, 'showAdditionalHistoricalYear', false);
      const isVisibleColumn = isAdditionalHistoricalYear
        ? isAdditionalHistoricalYear && showAdditionalHistoricalYear
        : null;

      cells = {
        ...cells,
        [key]: {
          ...row,
          alias: getValueByPropOrFallback(row, 'alias', ''),
          childIndex: column.quarter,
          className: dateInLTMOrNTM ? 'date-editor' : row.className,
          columnId: column.id,
          columnLegend,
          isAdditionalHistoricalYear,
          isParent: getValueByPropOrFallback(column, 'isParent', false),
          isParentWithDivider: Boolean(column.isParentWithDivider),
          isVisibleColumn,
          key,
          needsSpacer: Boolean(column.needsSpacer),
          rowSpan: getRowSpan(rowNumber, column),
          showAdditionalHistoricalYear,
          value: parsedValue,
          // any here as the value prop gets overwritten by the secondRowConditionalProps but is not(?) guaranteed to do so
          ...(secondRowConditionalProps as any),
        },
      };
    });
  });

  return cells;
};

export default parser;
